"use client";

import LoadingSpinner from "@/components/LoadingSpinner/LoadingSpinner";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "@/app/i18n/client";
import { BsSearch as SearchIcon } from "react-icons/bs";
import { MdClear as ClearIcon } from "react-icons/md";
import {
  QueryFunctionContext,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import SearchResultError from "./SearchResultError";
import { useDebounce } from "@/utils/hooks/use-debounce";
import { cn } from "@/lib/utils";

export type SearchProps =
  | {
      id: string;
      score: number; // A score for ordering the results.
      value: string;
    }
  | {
      id: number;
      score: number;
      value: string;
    };

type SearchFieldProps<T extends SearchProps> = {
  inputId: string;
  inputOnSelect: "value" | "cleared"; //
  onClear?: () => void; // Used to enable a clear button next to the input.
  httpFetchMethod: (
    searchTerm: string,
    abortSignal?: AbortSignal,
  ) => Promise<T[]> | T[];
  onSelect: (elmenent: T) => void;
  resultGenerator: (element: T) => React.ReactNode;
  placeholder?: string;
  className?: string;
  enableQuery?: (searchTerm: string) => boolean;
  disabled?: boolean;
  delayQuery?: number;
  maxQueryResults?: number;
  minCharacters?: number;
  initialValue?: string;
  disableSearchIcon?: boolean;
  searchResultsClassName?: string; // The div wrapped around a ul
  inputClassName?: string;
  "data-sentry-unmask"?: boolean;
};

/**
 * SearchField is a multi-purpose Element for Searching and Selecting. It abstracts away error handling, loading, and timeouts.
 * It features two modes for value selection: "value" and "cleared". This defines the behaviour of the input when a result is selected. It either cleares the input or sets the input to the selected value without searching it again.
 *
 * @param props
 * @returns
 */
export default function SearchField<T extends SearchProps>(
  props: SearchFieldProps<T>,
) {
  const { t } = useTranslation();
  const [lastSelectedResult, setLastSelectedResult] = useState<T | undefined>();
  const [inputValue, setInputValue] = useState<{
    value: string;
    searchThis: boolean;
  }>(
    props.initialValue
      ? { value: props.initialValue, searchThis: false }
      : { value: "", searchThis: false },
  );

  useEffect(() => {
    if (props.initialValue) {
      setInputValue({ value: props.initialValue, searchThis: false });
    }
  }, [props.initialValue]);

  const searchTerm = useMemo(() => {
    return inputValue.searchThis
      ? props.minCharacters
        ? inputValue.value.length >= props.minCharacters
          ? inputValue.value
          : ""
        : inputValue.value
      : "";
  }, [inputValue, props.minCharacters]);
  const debouncedSearch = useDebounce(searchTerm, props.delayQuery ?? 0);
  const [showAllResults, setShowAllResults] = useState(false);
  const [showResultList, setShowResultList] = useState(false);

  const {
    isLoading,
    data: results,
    isError,
    error,
  } = useQuery({
    queryKey: [props.inputId, { searchTerm: debouncedSearch }],
    queryFn: async (
      context: QueryFunctionContext<[string, { searchTerm: string }]>,
    ) => {
      const results = await props.httpFetchMethod(
        context.queryKey[1].searchTerm,
        context.signal,
      );
      setShowResultList(true);
      return results;
    },
    enabled: !props.disabled && !!searchTerm,
  });

  /**
   * searchResults is an Array of JSX Elements ready for rendering into an unordered list. It is a list element with a button for each search result.
   * The button defines Key Events for navigating through the results and selecting the current result.
   */
  const searchResults = useMemo(() => {
    function Suggestion(searchResult: T, index: number) {
      const scrollPreventer = (e: KeyboardEvent) => {
        if (e.key === "ArrowDown") {
          e.preventDefault();
        }
      };
      return (
        <li
          id={!isError ? String(searchResult.id) : error.name}
          key={!isError ? searchResult.id : error.name}
        >
          <button
            type="button"
            className="w-full"
            onClick={() => {
              if (!isError) {
                setLastSelectedResult(searchResult);
                props.onSelect(searchResult);
                setInputValue({ value: searchResult.value, searchThis: false });
              }
              ResetResultList();
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                e.stopPropagation();
                if (!isError) {
                  setLastSelectedResult(searchResult);
                  props.onSelect(searchResult);
                }
                ResetResultList();
              }
            }}
            onKeyUp={(e) => {
              if (e.key === "ArrowDown") {
                const thisElement = e.currentTarget;
                console.log("Targeting " + thisElement.parentElement?.id);
                const nextElement = (thisElement.parentElement
                  ?.nextElementSibling?.firstChild ?? undefined) as
                  | HTMLElement
                  | undefined;
                nextElement?.focus();
              } else if (e.key === "ArrowUp") {
                const thisElement = e.currentTarget;
                const previousElement = (thisElement.parentElement
                  ?.previousElementSibling?.firstChild ?? undefined) as
                  | HTMLElement
                  | undefined;
                if (previousElement) {
                  previousElement.focus();
                } else {
                  (
                    document.getElementById(props.inputId) as HTMLInputElement
                  ).focus();
                }
              } else if (e.key === "Escape") {
                ResetResultList();
                setInputValue({ value: "", searchThis: false });
              }
            }}
            onFocus={(e) => {
              if (!e.currentTarget.parentElement?.nextElementSibling) {
                window.addEventListener("keydown", scrollPreventer);
              }
            }}
            onBlur={(e) => {
              if (!e.currentTarget.parentElement?.nextElementSibling) {
                window.removeEventListener("keydown", scrollPreventer);
              }
            }}
            tabIndex={!isError ? searchResult.score : index}
          >
            {props.resultGenerator(searchResult)}
          </button>
        </li>
      );
    }
    if (isError) {
      return [
        <li key="error">
          <SearchResultError error={error} design="new" />
        </li>,
      ];
    }
    if (!results) {
      return [];
    }
    return Array.from(results).map((r, i) => Suggestion(r, i));
  }, [results, props]);

  const queryClient = useQueryClient();

  function ResetResultList() {
    queryClient.cancelQueries({ queryKey: [props.inputId] });
    setShowResultList(false);
    setShowAllResults(false);
  }

  const filteredItems = useMemo(() => {
    if (isLoading) {
      return (
        <li key={"loading"}>
          <div className="relative h-[5em]">
            <LoadingSpinner embedded={true} small={true} />
          </div>
        </li>
      );
    }
    if (isError) {
      return (
        <li key="error">
          <SearchResultError
            error={error}
            design="new"
            className={props.className}
          />
        </li>
      );
    }
    if (isError) {
      return searchResults;
    }
    const itemsToReturn = searchResults.filter((r, i) =>
      props.maxQueryResults ? i < props.maxQueryResults : true,
    );
    return showAllResults
      ? searchResults
      : itemsToReturn.length < searchResults.length
        ? itemsToReturn.concat(
            <li id="expand" key={"expand"}>
              <button
                type="button"
                onClick={(e) => {
                  (
                    e.currentTarget.parentElement?.previousElementSibling
                      ?.firstElementChild as HTMLButtonElement | undefined
                  )?.focus();
                  setShowAllResults(true);
                }}
              >
                {t("MAIN.CURRENT.MORE")}
              </button>
            </li>,
          )
        : itemsToReturn;
  }, [isLoading, searchResults, showAllResults, t, props.maxQueryResults]);

  return (
    <div
      className={cn("twSearchField relative", props.className)}
      onBlur={(e) => {
        queryClient.cancelQueries({ queryKey: [props.inputId] });
        // Only reset if the current target is not one of the results
        console.log(
          "Target: " +
            e.currentTarget.tagName +
            ", " +
            e.currentTarget.className,
        );
        console.log(
          "Related Target: " +
            e.relatedTarget?.tagName +
            ", " +
            e.relatedTarget?.className,
        );
        if (
          (!e.currentTarget.contains(e.relatedTarget) &&
            e.currentTarget !== e.relatedTarget) ||
          !e.relatedTarget
        ) {
          if (props.inputOnSelect === "cleared") {
            ResetResultList();
            setInputValue({ value: "", searchThis: false });
          } else {
            console.log("Cancel query and don't show results");
            queryClient.cancelQueries({ queryKey: [props.inputId] });
            setShowAllResults(false);
            setShowResultList(false);
          }
        }
      }}
      data-sentry-unmask={props["data-sentry-unmask"]}
    >
      {!props.disableSearchIcon && (
        <SearchIcon
          className="pointer-events-none absolute left-2 top-1/2 z-[2] translate-y-[-50%]"
          pointerEvents={"none"}
        />
      )}
      <input
        type={"text"}
        className={cn("w-full pl-8", props.inputClassName)}
        placeholder={props.placeholder}
        disabled={props.disabled}
        id={props.inputId}
        value={inputValue.value}
        autoComplete="off"
        onChange={(e) => {
          const { value } = e.target;
          setShowAllResults(false);
          setInputValue({ value: value, searchThis: true });
        }}
        onKeyUp={(e) => {
          if (e.key === "Escape") {
            if (inputValue.value === "") {
              const focusedElement = document.activeElement as HTMLInputElement;
              focusedElement.blur();
            }
            ResetResultList();
            setInputValue({ value: "", searchThis: false });
          } else if (e.key === "ArrowDown") {
            // Grab the searchResults ul, then the first li below it and then the button inside, as defined by the searchResults useMemo function
            const firstListElement = (document.getElementById(
              props.inputId + "searchResultsList",
            )?.firstElementChild?.firstElementChild ?? undefined) as
              | HTMLButtonElement
              | undefined;
            firstListElement?.focus();
          }
        }}
      />
      {props.onClear && (
        <button
          type="button"
          className="absolute right-0 top-[50%] flex h-6 w-6 translate-y-[-50%] items-center justify-center rounded-none bg-white p-0"
          onClick={() => {
            ResetResultList();
            setLastSelectedResult(undefined);
            setInputValue({ value: "", searchThis: false });
            props.onClear?.();
          }}
          onKeyUp={(e) => {
            if (e.key === "Escape") {
              setInputValue({ value: "", searchThis: false });
              const inputElement = document.getElementById(
                props.inputId,
              ) as HTMLInputElement;
              inputElement.focus();
            }
          }}
        >
          <ClearIcon />
        </button>
      )}
      <div
        className={cn(
          "searchResults absolute bottom-0 z-10 translate-y-full rounded-b-[1rem] bg-white",
          !showResultList && "hidden",
          props.searchResultsClassName,
        )}
      >
        <ul
          className="my-0 list-none pl-0"
          id={props.inputId + "searchResultsList"}
        >
          {showResultList ? filteredItems : null}
        </ul>
      </div>
    </div>
  );
}
